import { Alert, useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import {
  Box,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  Switch,
} from '@mui/material'
import AlertNotificationTypeSelect from '../../form/AlertNotificationTypeSelect'
import ScheduleSelect from '../../form/ScheduleSelect'
import { normalizeSchedule, convertScheduleTz } from '../../../common/backtestSchedule'
import { getClientTimeZone } from '../../../common/timezones'
import PatternSelect from '../../form/PatternSelect'
import { PatternVersionSelector } from '../../form/PatternVersionSelector'
import { ISymbol } from '../../../common/components/dialogs/symbolSelectDialog/SymbolSelect'
import { useRef, useEffect, useState } from 'react'
import { usePattern } from '../../../hooks/usePattern'
import { setNestedAlertSymbol } from '../../../common/utils/setNestedAlertSymbol'
import {
  isSymbolSelectionDisabled,
  AlertFormSettings,
} from '../../../common/utils/isSymbolSelectionDisabled'
import SymbolSelectManager, {
  ISymbolSelectManagerRef,
} from '../../../common/components/dialogs/symbolSelectDialog/SymbolSelectManager'

function AlertNotificationTimeOfDayForm({
  alert,
  onAlertKeyChange,
  settings,
}: {
  alert: Alert
  onAlertKeyChange: (key: string, value: any) => void
  settings: AlertFormSettings
}) {
  const { usermeta, tradingHouse } = useApplicationSuiteContext()

  const { symbols } = useApplicationSuiteContext()
  const managerRef = useRef<ISymbolSelectManagerRef | null>(null)

  // A template-managed alert's pattern always tracks its template's pattern on the
  // backend (syncAlertsOnStandardAlertTemplateUpdate ignores autoUpdatePattern for
  // these and force-writes it true) — the toggle must reflect that as locked-on
  // rather than let the member believe they can freeze it.
  const isTemplateManaged = !!alert.alertTemplateId

  const symbolSelectionDisabled = isSymbolSelectionDisabled(alert, settings)
  const patternSelectionDisabled =
    alert.from === 'pattern' || alert.from === 'backtest' || settings?.patternDisabled
  // Picking a different pattern version is a much smaller, safer action than
  // changing to a different pattern entirely, so it has its own disabled flag —
  // it must NOT inherit settings?.patternDisabled or the 'pattern' origin check,
  // otherwise the version selector renders but stays inert on an existing alert.
  const versionSelectionDisabled = alert.from === 'backtest'

  // Track the requested version separately to avoid update loops
  const [requestedVersion, setRequestedVersion] = useState<number | undefined>(
    alert.pattern?.version
  )
  // Reported by PatternVersionSelector from the pattern's real version history;
  // used to resync to latest when the auto-update toggle is switched on.
  const [latestPatternVersion, setLatestPatternVersion] = useState<number | undefined>(
    alert.pattern?.version
  )
  // Reported by PatternVersionSelector while it's still resolving version history —
  // until then, latestPatternVersion is just a seed, not the real latest version.
  const [versionHistoryLoading, setVersionHistoryLoading] = useState(true)

  const handleAutoUpdateToggle = (checked: boolean) => {
    onAlertKeyChange('autoUpdatePattern', checked)
    if (
      checked &&
      latestPatternVersion !== undefined &&
      alert.pattern?.version !== latestPatternVersion
    ) {
      setRequestedVersion(latestPatternVersion)
    }
  }

  // Fetch the versioned pattern when requested version changes
  const [versionedPattern] = usePattern(alert.pattern?.id, requestedVersion, usermeta.gcid)

  // Update alert pattern when versioned pattern is fetched (only when version actually changed)
  useEffect(() => {
    if (
      versionedPattern &&
      alert.pattern?.id === versionedPattern.id &&
      versionedPattern.version === requestedVersion &&
      alert.pattern?.version !== requestedVersion
    ) {
      // Update the full pattern object with the versioned pattern,
      // preserving the alert's symbol across all nested chartDetails
      const updatedPattern = structuredClone(versionedPattern)
      if (alert.symbol) {
        setNestedAlertSymbol(updatedPattern, alert.symbol)
      }
      onAlertKeyChange('pattern', updatedPattern)
    }
  }, [
    versionedPattern,
    requestedVersion,
    alert.pattern?.id,
    alert.pattern?.version,
    alert.symbol,
    onAlertKeyChange,
  ])
  const daySelectionDisabled =
    usermeta.tradingHouses[tradingHouse]?.tpaAffiliateAlertId === alert.id
  const timeOfDaySelectionDisabled =
    usermeta.tradingHouses[tradingHouse]?.tpaAffiliateAlertId === alert.id

  const viewerTz = getClientTimeZone()
  const localSchedule = convertScheduleTz(normalizeSchedule(alert), viewerTz)

  const selectSymbol = (symbolChosen: ISymbol | undefined) => {
    onAlertKeyChange('symbol', symbolChosen?.name)
  }

  // Check if pattern and symbol are empty for conditional styling
  const isPatternEmpty = !alert.pattern
  const isSymbolEmpty = !alert.symbol

  return (
    <>
      <Box display={'flex'} flexDirection={'column'} gap={2}>
        <TextField
          label={'Alert Name'}
          value={alert.name}
          InputProps={{ sx: { height: '40px' } }}
          onChange={(event) => onAlertKeyChange('name', event.target.value)}
        />

        <Box
          sx={{
            '& .MuiInputBase-root': { height: '40px' },
          }}
        >
          <PatternSelect
            pattern={alert.pattern}
            onSelect={(pattern) => onAlertKeyChange('pattern', pattern)}
            disabled={patternSelectionDisabled}
            error={{
              hasError: isPatternEmpty,
              message: 'A pattern must be selected',
            }}
          />
          {alert.pattern?.id && (
            <>
              <PatternVersionSelector
                patternId={alert.pattern.id}
                gcid={usermeta.gcid}
                tradingHouse={alert.pattern?.tradingHouse}
                currentVersion={alert.pattern.version}
                selectedVersion={requestedVersion}
                onVersionSelect={(version) => {
                  if (!alert.pattern) return
                  const resolvedVersion = version || alert.pattern.version
                  // Set requested version - usePattern hook + useEffect will fetch and update the full pattern
                  setRequestedVersion(resolvedVersion)
                  // A manual pin to a non-latest version overrides auto-update — otherwise
                  // the backend trigger would silently overwrite this choice back to latest
                  // on the next pattern update.
                  if (
                    alert.autoUpdatePattern &&
                    latestPatternVersion !== undefined &&
                    resolvedVersion !== latestPatternVersion
                  ) {
                    onAlertKeyChange('autoUpdatePattern', false)
                  }
                }}
                onLatestVersionResolved={setLatestPatternVersion}
                onLoadingChange={setVersionHistoryLoading}
                disabled={versionSelectionDisabled}
              />
              {alert.pattern?.tradingHouse && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mt: 1 }}
                >
                  <Typography variant="body2">Automatically use latest pattern version</Typography>
                  <Switch
                    checked={isTemplateManaged || !!alert.autoUpdatePattern}
                    onChange={(event) => handleAutoUpdateToggle(event.target.checked)}
                    disabled={
                      isTemplateManaged || versionSelectionDisabled || versionHistoryLoading
                    }
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </Box>

        <Box
          sx={{
            '& .MuiInputBase-root': { height: '40px' },
          }}
        >
          <>
            <SymbolSelectManager
              ref={managerRef}
              caller={'symbolSelect'}
              symbolSelectCallback={selectSymbol}
              symbols={(symbols || []) as ISymbol[]}
              currentSymbol={alert.symbol}
            />
            <FormControl variant="outlined" fullWidth>
              <InputLabel
                error={isSymbolEmpty}
                id="symbol"
                sx={{
                  '&:not(.MuiInputLabel-shrink)': {
                    transform: 'translate(14px, 10px) scale(1)',
                  },
                }}
              >
                Symbol
              </InputLabel>
              <Select
                labelId="symbol"
                value={alert.symbol || ''}
                name="symbol"
                label="Symbol"
                open={false}
                onClick={() => {
                  if (managerRef.current && !symbolSelectionDisabled) {
                    managerRef.current.openDialog()
                  }
                }}
                disabled={symbolSelectionDisabled}
                error={isSymbolEmpty}
              >
                {(symbols || []).map((symbol: any, index) => (
                  <MenuItem key={index} value={symbol.name}>
                    {symbol.name}
                  </MenuItem>
                ))}
              </Select>
              {isSymbolEmpty && <FormHelperText error>A symbol must be selected</FormHelperText>}
            </FormControl>
          </>
        </Box>

        <Typography variant={'body2'}>Schedule</Typography>
        <ScheduleSelect
          timezone={localSchedule.timezone}
          windows={localSchedule.windows}
          onWindowsChange={(windows) =>
            onAlertKeyChange('schedule', { timezone: viewerTz, windows })
          }
          disabled={timeOfDaySelectionDisabled || daySelectionDisabled}
        />

        <Typography variant={'body2'}>Notification Method</Typography>
        <AlertNotificationTypeSelect
          alert={alert}
          onNotificationTypeChange={(key, value) => onAlertKeyChange(key, value)}
        />
      </Box>
    </>
  )
}

export default AlertNotificationTimeOfDayForm
